import { MissingModule } from './errors';
import { System } from './System';

export { System } from './System';

/**
 * General
 */

export namespace Lifecycle {
  export type moduleDidLoad<
    BundleName extends string,
    ModuleName extends string,
    BundlesModules extends { [K in BundleName]: ModuleName },
    ModulesExport extends { [K in ModuleName]: any }
  > = (system: System<BundleName, ModuleName, BundlesModules, ModulesExport>) => void | Promise<any>;
  export type moduleWillLoad<
    BundleName extends string,
    ModuleName extends string,
    BundlesModules extends { [K in BundleName]: ModuleName },
    ModulesExport extends { [K in ModuleName]: any }
  > = (system: System<BundleName, ModuleName, BundlesModules, ModulesExport>) => void | Promise<any>;
  export type moduleWillUnload<
    BundleName extends string,
    ModuleName extends string,
    BundlesModules extends { [K in BundleName]: ModuleName },
    ModulesExport extends { [K in ModuleName]: any }
  > = (system: System<BundleName, ModuleName, BundlesModules, ModulesExport>) => void | Promise<any>;
}

export type Lifecycle<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any }
> = {
  moduleDidLoad: Lifecycle.moduleDidLoad<BundleName, ModuleName, BundlesModules, ModulesExport>;
  moduleWillLoad: Lifecycle.moduleWillLoad<BundleName, ModuleName, BundlesModules, ModulesExport>;
  moduleWillUnload: Lifecycle.moduleWillUnload<BundleName, ModuleName, BundlesModules, ModulesExport>;
};

export enum BundleStatus {
  Void = 'void',
  Fetching = 'fetching',
  Fetched = 'fetched',
  Parsing = 'parsing',
  Parsed = 'parsed',
  Error = 'error',
}

export enum ModuleStatus {
  Void = 'void',
  Fetching = 'fetching',
  Fetched = 'fetched',
  Parsing = 'parsing',
  Parsed = 'parsed',
  Loading = 'loading',
  Loaded = 'loaded',
  Error = 'error',
}

/**
 * Modules & Bundles
 */

export type ModulesOfBundles<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  B extends BundleName
> = BundlesModules[B];
export type ExportOfModule<
  ModuleName extends string,
  ModulesExport extends { [K in ModuleName]: any },
  M extends ModuleName
> = ModulesExport[M];
export type ModuleRequirer<
  ModuleName extends string,
  ModulesExport extends { [K in ModuleName]: any },
  M extends ModuleName
> = () => ExportOfModule<ModuleName, ModulesExport, M>;

export type ExportOfBundle<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any },
  B extends BundleName
> = {
  getCache: () => any;
  modules: {
    [M in ModulesOfBundles<BundleName, ModuleName, BundlesModules, B>]: () => ModuleRequirer<
      ModuleName,
      ModulesExport,
      M
    >
  };
};

export type PickModule<ModuleName extends string, K extends ModuleName> = K;

export type ModuleIndex = number;

export type BundlesTopology<BundleName extends string, ModuleName extends string> = {
  modules: Array<ModuleName>;
  bundles: { [B in BundleName]: Array<ModuleIndex> };
  dependencies: { [key: number]: Array<ModuleIndex> };
  entry: ModuleName;
};

export type ResolvedBundlesTopology<BundleName extends string, ModuleName extends string> = {
  modules: Array<ModuleName>;
  bundles: { [B in BundleName]: Array<ModuleName> };
  dependencies: { [K in ModuleName]: Array<ModuleName> };
  entry: ModuleName;
};

/**
 * Store
 */

export type Action<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any }
> = (
  s: State<BundleName, ModuleName, BundlesModules, ModulesExport>,
  select: Select<BundleName, ModuleName, BundlesModules, ModulesExport>
) => State<BundleName, ModuleName, BundlesModules, ModulesExport>;
export type Listener<ModuleName extends string> = (systemState: SystemPublicState<ModuleName>) => void;
export type Unsubsrcibe = () => void;
export type Selector<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any },
  O
> = (
  s: State<BundleName, ModuleName, BundlesModules, ModulesExport>,
  select: Select<BundleName, ModuleName, BundlesModules, ModulesExport>
) => O;
export type Select<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any }
> = <O>(
  selector: Selector<BundleName, ModuleName, BundlesModules, ModulesExport, O>,
  overrideState: State<BundleName, ModuleName, BundlesModules, ModulesExport>
) => O;
export type SelectCurrent<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any }
> = <O>(selector: Selector<BundleName, ModuleName, BundlesModules, ModulesExport, O>) => O;

/**
 * State
 */

export type BlobUrl = string;

export type BundleState<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any },
  B extends BundleName
> = {
  fetching: Promise<BlobUrl> | null;
  fetched: BlobUrl | null;
  parsing: Promise<ExportOfBundle<BundleName, ModuleName, BundlesModules, ModulesExport, B>> | null;
  parsed: ExportOfBundle<BundleName, ModuleName, BundlesModules, ModulesExport, B> | null;
  error: any | null;
};

export type MissingDependenciesMap<ModuleName extends string> = {
  [M in ModuleName]?: MissingModule<ModuleName, ModuleName>
};

export type ModuleState<
  ModuleName extends string,
  ModulesExport extends { [K in ModuleName]: any },
  M extends ModuleName
> = {
  loading: Promise<ExportOfModule<ModuleName, ModulesExport, M>> | null;
  loadingReason: ModuleName | null;
  loaded: ExportOfModule<ModuleName, ModulesExport, M> | null;
  error: any | null;
  dependencies: Array<ModuleName>;
  missingDependencies: MissingDependenciesMap<ModuleName>;
};

export type State<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any }
> = {
  bundles: { [B in BundleName]?: BundleState<BundleName, ModuleName, BundlesModules, ModulesExport, B> };
  modules: { [M in ModuleName]?: ModuleState<ModuleName, ModulesExport, M> };
  booted: boolean;
  loadingModule: ModuleName | null;
  topology: ResolvedBundlesTopology<BundleName, ModuleName>;
};

/**
 * SystemPublicState
 */

export type ModulesStatus<ModuleName extends string> = { [K in ModuleName]?: ModuleStatus };
export type ModulesReasons<ModuleName extends string> = { [K in ModuleName]?: Array<ModuleName> };

export type SystemPublicState<ModuleName extends string> = {
  modulesStatus: ModulesStatus<ModuleName>;
  modulesReasons: ModulesReasons<ModuleName>;
};

/**
 * Error
 */

export type ModuleWithError<ModuleName extends string> = { name: ModuleName; error: MissingModule<ModuleName, any> };



// WEBPACK FOOTER //
// ./node_modules/front-system/engine/types.ts